// Обновление приложения после выкатки: веб (PWA) и встроенные браузеры мессенджеров.
//
// В Telegram и MAX приложение открывается во встроенном браузере (webview), который
// мессенджер держит живым в фоне. С service worker'ом (PWA, #444) новая версия там ставилась
// «в ожидание» и не включалась никогда: webview не закрывается, плашку «Обновить» не
// замечают — пользователи сидели на старой версии. Поэтому во встроенных браузерах
// работаем без service worker'а (офлайн там не нужен): index.html отдаётся nginx'ом с
// no-store, и каждый запуск берёт актуальную версию. Плюс сверка версии с сервером — для
// webview, который не перезагружается, пока висит в фоне.

const LOAD_AT = Date.now();
/** Окно после запуска, когда можно обновиться без спроса: пользователь ещё ничего не начал. */
export const AUTO_UPDATE_WINDOW_MS = 30_000;

let interacted = false;
if (typeof window !== "undefined") {
  const mark = () => { interacted = true; };
  window.addEventListener("pointerdown", mark, { once: true, capture: true });
  window.addEventListener("keydown", mark, { once: true, capture: true });
}

/** Обновиться можно без спроса: только что запустились и пользователь ещё ничего не трогал. */
export function canUpdateSilently(): boolean {
  return !interacted && Date.now() - LOAD_AT < AUTO_UPDATE_WINDOW_MS;
}

const SESSION_WEBVIEW = "anyskin.embedded";

/** Встроенный браузер мессенджера / приложения (Telegram, MAX, VK, Android WebView…). */
export function isEmbeddedWebview(): boolean {
  if (typeof window === "undefined") return false;
  try { if (sessionStorage.getItem(SESSION_WEBVIEW) === "1") return true; } catch { /* нет storage */ }
  const w = window as any;
  const ua = navigator.userAgent || "";
  const hit =
    !!w.Telegram?.WebApp?.initData || !!w.TelegramWebviewProxy || !!w.TelegramWebviewProxyProto ||
    /tgWebApp/i.test(location.hash + location.search) ||
    !!w.WebApp?.initData ||                                   // MAX Bridge
    /Telegram|MAX\/|MaxMessenger|OneMe|VKClient|VKAndroidApp|FBAN|FBAV|Instagram/i.test(ua) ||
    /; wv\)/.test(ua);                                        // любой Android WebView
  if (hit) { try { sessionStorage.setItem(SESSION_WEBVIEW, "1"); } catch { /* ignore */ } }
  return hit;
}

/** Снимает service worker и его кэши. true — страницей управлял service worker (она из кэша). */
export async function removeServiceWorker(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  const controlled = !!navigator.serviceWorker.controller;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* не критично: следующий запуск попробует снова */ }
  return controlled;
}

const BUNDLE_RE = /\/assets\/index-[\w-]+\.js/;

/** Главный бандл, на котором работает открытая страница (в dev — null). */
export function currentBundle(): string | null {
  const s = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'))
    .map((x) => new URL(x.src, location.href).pathname).find((p) => BUNDLE_RE.test(p));
  return s || null;
}

/** Главный бандл свежей версии на сервере: index.html с параметром мимо кэшей. */
export async function latestBundle(): Promise<string | null> {
  try {
    const r = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    return (await r.text()).match(BUNDLE_RE)?.[0] || null;
  } catch { return null; }
}

/** На сервере другая версия, чем открыта. null — не удалось понять (dev, офлайн). */
export async function isOutdated(): Promise<boolean | null> {
  const cur = currentBundle();
  if (!cur) return null;
  const latest = await latestBundle();
  return latest ? latest !== cur : null;
}

/** Перезагрузка не чаще раза в минуту — защита от цикла, если что-то пойдёт не так. */
export function reloadOnce(reason: string) {
  const KEY = "anyskin.reloadAt";
  try {
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch { /* без storage — всё равно перезагружаем один раз */ }
  console.info("[anyskin] перезагрузка:", reason);
  location.reload();
}

/** Страница открыта, но на сервере другая версия: чистим кэши service worker и
 *  перезагружаемся. Вызывается при запуске — работу это не прерывает. */
export async function hardRefreshIfStale(): Promise<boolean> {
  if (await isOutdated() !== true) return false;
  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    } catch { /* кэш недоступен — перезагрузка всё равно возьмёт свежий index.html */ }
  }
  reloadOnce("при запуске на сервере оказалась новая версия");
  return true;
}

/** Обновление, которое ждёт своей минуты: пока брокер работает — не трогаем, ушёл со
 *  вкладки или отложил на час — применяем сами. Возвращает функцию отмены. */
export function applyWhenIdle(apply: () => void, maxWaitMs = 60 * 60 * 1000): () => void {
  const at = Date.now();
  const onHidden = () => { if (document.visibilityState === "hidden") apply(); };
  document.addEventListener("visibilitychange", onHidden);
  const timer = setInterval(() => { if (Date.now() - at >= maxWaitMs) apply(); }, 60_000);
  return () => { document.removeEventListener("visibilitychange", onHidden); clearInterval(timer); };
}

/** До рендера: во встроенном браузере снимаем service worker; на вебе сверяем версию с
 *  сервером и уходим на свежую; после выкатки чанк раздела со старым именем не находится —
 *  тоже перезагружаемся. */
export function bootUpdateGuards() {
  if (isEmbeddedWebview()) {
    void removeServiceWorker().then((controlled) => {
      if (controlled) reloadOnce("встроенный браузер: страница была из кэша service worker");
    });
  } else {
    // Старый service worker мог отдать страницу из кэша — на запуске это видно и лечится
    // сразу, не дожидаясь, пока пользователь заметит плашку.
    void hardRefreshIfStale();
  }
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault();
    reloadOnce("не загрузился модуль после выкатки");
  });
}
